import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import StarIcon from "@mui/icons-material/Star";
import { saveCoachExerciseEvaluation } from "./funtionsBD/Mainservice";
import { localizedExerciseName } from "../utils/localizedCatalogFields";
import "./CoachExerciseEvalRoster.css";

function drillName(player, lang) {
  return localizedExerciseName(player?.personalized_exercise, lang);
}

function playerAutoevalScore(player) {
  const raw =
    player?.auto_evaluation_data?.exercise_perf ??
    player?.auto_evaluation_data?.evaluation_data?.exercise_perf;
  const n = Number(raw);
  return n >= 1 && n <= 5 ? n : null;
}

export function CoachExerciseEvalRoster({
  teId,
  players = [],
  highlightPlayerId = null,
  lang = "en",
}) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [error, setError] = useState("");

  const roster = useMemo(() => {
    const list = Array.isArray(players) ? players.filter((p) => p?.player_id) : [];
    const onExercise = list.filter((p) => p.on_this_exercise);
    const source = onExercise.length ? onExercise : list;
    return [...source].sort((a, b) =>
      String(a.player_name || "").localeCompare(String(b.player_name || ""))
    );
  }, [players]);

  useEffect(() => {
    const next = {};
    for (const player of roster) {
      const perf = Number(player.coach_evaluation?.exercise_perf);
      next[String(player.player_id)] = {
        perf: perf >= 1 && perf <= 5 ? perf : 0,
        notes: player.coach_evaluation?.notes || "",
      };
    }
    setDrafts(next);
  }, [roster]);

  const updateDraft = (playerId, patch) => {
    setSavedId(null);
    setDrafts((prev) => ({
      ...prev,
      [playerId]: { perf: 0, notes: "", ...prev[playerId], ...patch },
    }));
  };

  const savePlayer = async (player) => {
    const playerId = String(player.player_id);
    const draft = drafts[playerId] || { perf: 0, notes: "" };
    if (!teId || !draft.perf) return;
    setSavingId(playerId);
    setError("");
    try {
      await saveCoachExerciseEvaluation({
        te_id: teId,
        player_id: playerId,
        exercise_perf: draft.perf,
        notes: draft.notes || "",
      });
      setSavedId(playerId);
    } catch (err) {
      setError(err?.message || t("coachEvalSaveFailed", "Could not save this evaluation."));
    } finally {
      setSavingId(null);
    }
  };

  if (!roster.length) {
    return (
      <p className="coach-eval-roster__empty">
        {t(
          "coachEvalRosterEmpty",
          "No players are assigned to this exercise yet."
        )}
      </p>
    );
  }

  return (
    <section className="coach-eval-roster" aria-label={t("coachEvalRosterTitle", "Performance by player")}>
      <h3 className="coach-eval-section-title">
        {t("coachEvalRosterTitle", "Performance by player")}
      </h3>
      <p className="coach-eval-roster__hint">
        {t(
          "coachEvalRosterHint",
          "Rate each player's performance. Personalized exercises are the priority; other competency blocks can be rated too."
        )}
      </p>
      <ul className="coach-eval-roster__list">
        {roster.map((player) => {
          const playerId = String(player.player_id);
          const draft = drafts[playerId] || { perf: 0, notes: "" };
          const autoeval = playerAutoevalScore(player);
          const name = player.player_name || t("player", "Player");
          const drill = drillName(player, lang);
          const highlighted =
            highlightPlayerId && String(highlightPlayerId) === playerId;
          return (
            <li
              key={playerId}
              className={`coach-eval-roster__row${highlighted ? " is-current" : ""}`}
            >
              <div className="coach-eval-roster__who">
                <strong>{name}</strong>
                {drill ? <span className="coach-eval-roster__drill">{drill}</span> : null}
                <span className="coach-eval-roster__auto">
                  {autoeval
                    ? t("coachEvalPlayerAutoeval", {
                        perf: autoeval,
                        defaultValue: "Auto-eval {{perf}}/5",
                      })
                    : t("coachEvalNoAutoeval", "No auto-evaluation yet")}
                </span>
              </div>
              <div className="coach-eval-roster__rate">
                <div className="coach-eval-roster__stars" role="group" aria-label={name}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="coach-eval-roster__star-btn"
                      onClick={() => updateDraft(playerId, { perf: star })}
                      aria-label={`${star}/5`}
                    >
                      <StarIcon
                        className={
                          star <= draft.perf
                            ? "coach-eval-roster__star is-on"
                            : "coach-eval-roster__star"
                        }
                      />
                    </button>
                  ))}
                </div>
                <input
                  className="coach-eval-roster__notes"
                  value={draft.notes}
                  placeholder={t("coachEvalNotes", "Note (optional)")}
                  onChange={(event) => updateDraft(playerId, { notes: event.target.value })}
                />
                <button
                  type="button"
                  className="coach-eval-roster__save"
                  disabled={!draft.perf || savingId === playerId}
                  onClick={() => savePlayer(player)}
                >
                  {savingId === playerId ? (
                    <CircularProgress size={14} style={{ color: "#111" }} />
                  ) : savedId === playerId ? (
                    t("coachEvalSaved", "Saved")
                  ) : (
                    t("coachEvalSave", "Save")
                  )}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {error ? <p className="coach-eval-roster__error">{error}</p> : null}
    </section>
  );
}
